import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { DafDonationDetails, OTHER_DAF_PROVIDER } from "@simplyk/common";

import { CopyRow } from "../CopyRow/CopyRow";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ExternalLink, Mail } from "@/icons/outlined";

interface DonationDetailsProps {
  details: DafDonationDetails;
  amount: string;
  providerName: string;
  providerUrl: string | null;
  providerEmail: string | null;
  providerGrantFormUrl?: string | null;
  providerWebsiteUrl?: string | null;
  donorFullName: string;
  logoUrl?: string | null;
  onOpenPortal: () => void;
  onContactProvider?: () => void;
  onCopy?: (field: "ein" | "address" | "reference") => void;
}

export const DonationDetails: FC<DonationDetailsProps> = ({
  details,
  amount,
  providerName,
  providerUrl,
  providerEmail,
  providerGrantFormUrl,
  providerWebsiteUrl,
  donorFullName,
  logoUrl,
  onOpenPortal,
  onContactProvider,
  onCopy,
}) => {
  const { t } = useTranslate();
  const organizationName =
    details.organizationLegalName === details.organizationDisplayName
      ? details.organizationLegalName
      : `${details.organizationLegalName} (${details.organizationDisplayName})`;
  const emailParameters = {
    amount,
    organization: organizationName,
    ein: details.organizationEin,
    address: details.organizationAddress,
    reference: details.reference,
    donor: donorFullName,
  };
  const emailLink = `mailto:${providerEmail ?? ""}?${new URLSearchParams({
    subject: t("common", "daf.emailSubject", { ...emailParameters, organization: details.organizationLegalName }),
    body: t("common", "daf.emailBody", emailParameters),
  })
    .toString()
    .replaceAll("+", "%20")}`;
  const hasNoChannel =
    !providerUrl && !providerEmail && !providerGrantFormUrl && details.providerId !== OTHER_DAF_PROVIDER.id;

  return (
    <Stack
      gap={1.5}
      padding={3}
      sx={(theme) => ({
        width: "100%",
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.formsComponents.block.cardBackgroundColor,
      })}
    >
      {logoUrl && (
        <Box
          component="img"
          src={logoUrl}
          alt={details.organizationDisplayName}
          sx={(theme) => ({
            width: theme.spacing(5.5),
            height: theme.spacing(5.5),
            borderRadius: theme.spacing(0.625),
            objectFit: "contain",
          })}
        />
      )}
      <Stack gap={1}>
        <FormTypography variant="subtitle1" vibe="moderate">
          {t("common", "daf.donationTo", { amount, organization: organizationName })}
        </FormTypography>
        <Stack gap={1}>
          <CopyRow label={t("common", "daf.ein")} value={details.organizationEin} onCopy={() => onCopy?.("ein")} />
          <CopyRow
            label={t("common", "daf.address")}
            value={details.organizationAddress}
            onCopy={() => onCopy?.("address")}
          />
          <CopyRow
            label={t("common", "daf.reference")}
            value={details.reference}
            onCopy={() => onCopy?.("reference")}
          />
        </Stack>
      </Stack>
      {providerUrl && (
        <FormButton
          variant="tonal"
          size="small"
          endIcon={<ExternalLink />}
          onClick={onOpenPortal}
          sx={{ alignSelf: "flex-start", maxWidth: "100%", whiteSpace: "normal", height: "auto" }}
        >
          {t("common", "daf.openPortal", { provider: providerName })}
        </FormButton>
      )}
      {providerGrantFormUrl && (
        <>
          <FormTypography variant="body2" vibe="moderate">
            {t("common", "daf.grantFormInstructions")}
          </FormTypography>
          <FormButton
            href={providerGrantFormUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="tonal"
            size="small"
            endIcon={<ExternalLink />}
            onClick={onContactProvider}
            sx={{ alignSelf: "flex-start", maxWidth: "100%", whiteSpace: "normal", height: "auto" }}
          >
            {t("common", "daf.openGrantForm")}
          </FormButton>
        </>
      )}
      {hasNoChannel && (
        <>
          <FormTypography variant="body2" vibe="moderate">
            {t("common", "daf.noOnlineChannel")}
          </FormTypography>
          {providerWebsiteUrl && (
            <FormButton
              href={providerWebsiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="tonal"
              size="small"
              endIcon={<ExternalLink />}
              onClick={onContactProvider}
              sx={{ alignSelf: "flex-start", maxWidth: "100%", whiteSpace: "normal", height: "auto" }}
            >
              {t("common", "daf.visitProviderWebsite", { provider: providerName })}
            </FormButton>
          )}
        </>
      )}
      {!providerUrl && (providerEmail || details.providerId === OTHER_DAF_PROVIDER.id) && (
        <FormButton
          href={emailLink}
          onClick={onContactProvider}
          variant="tonal"
          size="small"
          endIcon={<Mail />}
          sx={{ alignSelf: "flex-start", maxWidth: "100%", whiteSpace: "normal", height: "auto" }}
        >
          {t("common", "daf.contactProvider", { provider: providerName })}
        </FormButton>
      )}
    </Stack>
  );
};
